import { useCallback, useEffect, useRef, useState } from 'react';
import AxiosFactory from '../../services/http.service';
import ApiRoutes from '../../config/api.routes';
import useMetaMask from '../../contexts/useMetaMask';
import { IpfsDocument, IpfsDownloadUrls, TemplatePlaceholders } from './ipfsDocumentTypes';

interface UseIpfsDocumentSignerOptions {
  documentId: number;
}

export function useIpfsDocumentSigner({ documentId }: UseIpfsDocumentSignerOptions) {
  const { connectAndSign, currentAccount } = useMetaMask();

  const [document, setDocument] = useState<IpfsDocument | null>(null);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [downloadUrls, setDownloadUrls] = useState<IpfsDownloadUrls | null>(null);

  const [loadingDoc, setLoadingDoc] = useState(true);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  // Local drafts for the template-variable inputs, seeded from the document's saved values.
  const [varDrafts, setVarDrafts] = useState<Record<string, string>>({});
  const [saveVarsLoading, setSaveVarsLoading] = useState(false);
  const [saveVarsError, setSaveVarsError] = useState<string | null>(null);

  const [signLoading, setSignLoading] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const blobUrlRef = useRef<string>('');

  const refreshDocument = useCallback(async () => {
    try {
      const { axios } = new AxiosFactory();
      const res = await axios.get<IpfsDocument>(ApiRoutes.ipfsDocument.get(documentId));
      setDocument(res.data);
      return res.data;
    } catch (err: any) {
      setDocError(err?.response?.data?.message || err?.message || 'Failed to load document.');
      return null;
    }
  }, [documentId]);

  useEffect(() => {
    setLoadingDoc(true);
    refreshDocument().finally(() => setLoadingDoc(false));
  }, [refreshDocument]);

  // Merge saved values over the drafts so locked/filled fields always reflect the server while
  // any value the user has typed into a still-unfilled field is preserved across refreshes.
  useEffect(() => {
    if (!document) {return;}
    setVarDrafts((prev) => ({ ...prev, ...document.templateVars }));
  }, [document]);

  // Fetch the list of placeholder names for this document's type.
  useEffect(() => {
    const type = document?.type;
    if (!type) {return;}

    let active = true;
    setLoadingPlaceholders(true);
    const { axios } = new AxiosFactory();
    axios
      .get<TemplatePlaceholders>(ApiRoutes.ipfsDocument.templates(type))
      .then((res) => {
        if (active) {setPlaceholders(res.data.placeholders ?? []);}
      })
      .catch((err) => console.error('Failed to fetch template placeholders', err))
      .finally(() => {
        if (active) {setLoadingPlaceholders(false);}
      });

    return () => {
      active = false;
    };
  }, [document?.type]);

  // Fetch the draft preview PDF with auth and expose it as a local blob URL for rendering.
  const refreshPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const { axios } = new AxiosFactory();
      const res = await axios.get<ArrayBuffer>(ApiRoutes.ipfsDocument.preview(documentId), {
        responseType: 'arraybuffer',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (blobUrlRef.current) {URL.revokeObjectURL(blobUrlRef.current);}
      blobUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      console.error('Failed to fetch IPFS document preview', err);
    } finally {
      setLoadingPreview(false);
    }
  }, [documentId]);

  useEffect(() => {
    refreshPreview();
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = '';
      }
    };
  }, [refreshPreview]);

  const setVarDraft = useCallback((key: string, value: string) => {
    setVarDrafts((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Persist only the fields the caller is allowed to fill: unfilled tokens, or tokens this wallet
  // already owns, that differ from the saved value. The API rejects keys owned by another party.
  const handleSaveVars = useCallback(async () => {
    if (!document) {return;}
    setSaveVarsLoading(true);
    setSaveVarsError(null);
    try {
      const owners = document.templateVarOwners ?? {};
      const account = currentAccount.toLowerCase();

      const vars: Record<string, string> = {};
      placeholders.forEach((key) => {
        const draft = (varDrafts[key] ?? '').trim();
        if (!draft) {return;}
        const owner = owners[key]?.toLowerCase();
        const editable = !owner || owner === account;
        const changed = draft !== (document.templateVars[key] ?? '');
        if (editable && changed) {vars[key] = draft;}
      });

      if (Object.keys(vars).length === 0) {
        setSaveVarsError('No new field values to save.');
        return;
      }

      const { axios } = new AxiosFactory();
      await axios.patch(ApiRoutes.ipfsDocument.templateVars(documentId), { vars });
      await refreshDocument();
      await refreshPreview();
    } catch (e: any) {
      setSaveVarsError(e?.response?.data?.message || e?.message || 'Failed to save fields.');
    } finally {
      setSaveVarsLoading(false);
    }
  }, [document, placeholders, varDrafts, currentAccount, documentId, refreshDocument, refreshPreview]);

  const handleSign = useCallback(async () => {
    setSignLoading(true);
    setSignError(null);
    try {
      // Message format required by the API: "EcoFutures Document <id>: <unix-seconds>"
      const timestamp = Math.floor(Date.now() / 1000);
      const data = `EcoFutures Document ${documentId}: ${timestamp}`;

      const result = await connectAndSign(data);
      if (typeof result === 'string') {
        setSignError(result);
        return;
      }

      const sign = result.signature;

      const { axios } = new AxiosFactory();
      await axios.post(ApiRoutes.ipfsDocument.sign(documentId), { data, sign });
      await refreshDocument();
      await refreshPreview();
    } catch (e: any) {
      setSignError(e?.response?.data?.message || e?.message || 'Failed to sign document.');
      // The backend records the signature *before* it auto-finalizes, so a finalize failure (a
      // field left blank, say) still answers 400 with the signature on file. Re-read the document
      // so the signer view — and anything waiting on onSigned — reflects what actually happened.
      await refreshDocument();
    } finally {
      setSignLoading(false);
    }
  }, [connectAndSign, documentId, refreshDocument, refreshPreview]);

  const handleFinalize = useCallback(async () => {
    setFinalizeLoading(true);
    setFinalizeError(null);
    try {
      const { axios } = new AxiosFactory();
      await axios.post(ApiRoutes.ipfsDocument.finalize(documentId));
      await refreshDocument();
      await refreshPreview();
    } catch (e: any) {
      setFinalizeError(e?.response?.data?.message || e?.message || 'Failed to finalize document.');
    } finally {
      setFinalizeLoading(false);
    }
  }, [documentId, refreshDocument, refreshPreview]);

  const handleDownload = useCallback(async () => {
    setDownloadLoading(true);
    setDownloadError(null);
    try {
      const { axios } = new AxiosFactory();
      const res = await axios.get<IpfsDownloadUrls>(ApiRoutes.ipfsDocument.download(documentId));
      setDownloadUrls(res.data);
    } catch (e: any) {
      setDownloadError(e?.response?.data?.message || e?.message || 'Failed to fetch download URLs.');
    } finally {
      setDownloadLoading(false);
    }
  }, [documentId]);

  const allSigned =
    !!document &&
    document.requiredSigners.length > 0 &&
    document.requiredSigners.every((addr) =>
      document.signatures.some((s) => s.stakeholderWallet.toLowerCase() === addr.toLowerCase())
    );

  const allVarsFilled =
    placeholders.length > 0 &&
    placeholders.every((key) => (document?.templateVars?.[key] ?? '').trim() !== '');

  return {
    document,
    placeholders,
    previewUrl,
    downloadUrls,
    loadingDoc,
    loadingPlaceholders,
    loadingPreview,
    docError,
    varDrafts,
    setVarDraft,
    saveVarsLoading,
    saveVarsError,
    handleSaveVars,
    signLoading,
    signError,
    finalizeLoading,
    finalizeError,
    downloadLoading,
    downloadError,
    allSigned,
    allVarsFilled,
    handleSign,
    handleFinalize,
    handleDownload,
  };
}
